/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useEffectEvent, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { Checkbox, Input, Textarea } from '@root/shared/ui-kit'

import { reatomCreateSupportTicket } from '../atoms/reatom-create-ticket'

import { SupportDialogHeading } from './support-dialog-heading'

type SupportCreateTicketProps = {
  onDone: () => void
  onClose?: () => void
}

type SupportCreateTicketFormValues = {
  name: string
  email: string
  problemTitle: string
  problemDescription: string
  emailReplies: boolean
}

export const SupportCreateTicket: React.FC<SupportCreateTicketProps> = (
  props,
) => {
  const { onDone, onClose } = props
  const { t } = useTranslation()
  const model = useMemo(() => reatomCreateSupportTicket(), [])
  const [attachments, setAttachments] = useState<Array<string>>([])

  const [requestState] = useAtom(model)
  const create = useAction(model.execute)

  const { control, formState, handleSubmit } =
    useForm<SupportCreateTicketFormValues>({
      mode: 'all',
      defaultValues: {
        name: '',
        email: '',
        problemTitle: '',
        problemDescription: '',
        emailReplies: false,
      },
    })

  const handleSubmitInternal = handleSubmit((values) => {
    create({
      message: values.problemDescription,
      subject: values.problemTitle,
    })
  })

  const onDoneInternal = useEffectEvent(onDone)
  useEffect(() => {
    if (AsyncStateN.isSuccess(requestState)) {
      onDoneInternal()
    }
  }, [requestState])

  const handleAttachmentAdd = (newAttachments: Array<string>) => {
    setAttachments((prev) => [...prev, ...newAttachments])
  }
  const handleAttachmentRemove = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index))
  }

  return (
    <Root>
      <SupportDialogHeading onClose={onClose} />
      <Form onSubmit={handleSubmitInternal}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              {...field}
              label={t('support.form.name-label')}
              required
              size="h40"
              placeholder={t('support.form.name-placeholder')}
            />
          )}
        />
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <Input
              {...field}
              label={t('support.form.email-optional-label')}
              size="h40"
              placeholder={t('support.form.email-placeholder')}
            />
          )}
        />
        <Controller
          control={control}
          name="problemTitle"
          render={({ field }) => (
            <Input
              {...field}
              required
              label={t('support.form.problem-title-label')}
              size="h40"
              placeholder={t('support.form.problem-title-placeholder')}
            />
          )}
        />
        <Controller
          control={control}
          name="problemDescription"
          render={({ field }) => (
            <Textarea
              {...field}
              rows={3}
              required
              label={t('support.form.problem-description-label')}
              size="h40"
              placeholder={t('support.form.problem-description-placeholder')}
              symbolCount
              emojiPicker
              attachments={attachments}
              onAttachmentsAdd={handleAttachmentAdd}
              onAttachmentsRemove={handleAttachmentRemove}
              maxLength={500}
            />
          )}
        />
        <Controller
          control={control}
          name="emailReplies"
          render={({ field }) => (
            <EmailRepliesSubscription>
              <Checkbox
                id="tos-accepted"
                checked={field.value}
                onChange={field.onChange}
              />
              <label htmlFor="tos-accepted">
                {t('support.form.email-replies-checkbox')}
              </label>
            </EmailRepliesSubscription>
          )}
        />
        <Button
          color="primary"
          radius="rounded"
          size="h32"
          variant="solid"
          type="submit"
          disabled={!formState.isValid || AsyncStateN.isLoading(requestState)}
          busy={AsyncStateN.isLoading(requestState)}
        >
          {t('support.form.submit-create-ticket')}
        </Button>
      </Form>
    </Root>
  )
}

const Root = styled.div``

const Form = styled.form`
  display: grid;
  padding: 14px 12px;
  gap: 12px;
`

const EmailRepliesSubscription = styled.div`
  color: var(--text-9);

  display: grid;
  gap: 8px;
  grid-template-columns: auto 1fr;

  font-size: 11px;
  font-weight: 400;
  line-height: 16px;

  label {
    cursor: pointer;
  }
`
